import React, { useState, useEffect } from 'react';
import { Card, Upload, Button, Table, message, Tag, Space } from 'antd';
import { UploadOutlined, FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { keywordSearchAPI } from '../../../services/api';
import type { KeywordSearchImport as KeywordSearchImportType } from '../../../types';
import { useNavigate } from 'react-router-dom';

export const KeywordSearchImport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [imports, setImports] = useState<KeywordSearchImportType[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        loadImports();
    }, []);

    const loadImports = async () => {
        try {
            setLoading(true);
            const response = await keywordSearchAPI.listImports();
            // Ensure we're setting an array, handling potential pagination structure
            const data = response.data;
            if (Array.isArray(data)) {
                setImports(data);
            } else if ((data as any).results) {
                setImports((data as any).results);
            } else {
                setImports([]);
            }
        } catch (error) {
            console.error('Failed to load imports:', error);
            message.error('加载导入记录失败');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload: UploadProps['customRequest'] = async (options) => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file as File);

        try {
            await keywordSearchAPI.upload(formData);
            message.success('上传成功');
            onSuccess?.('ok');
            loadImports();
        } catch (error: any) {
            console.error('Upload failed:', error);
            message.error(error.response?.data?.error || '上传失败');
            onError?.(error);
        }
    };

    const columns = [
        {
            title: '关键词',
            dataIndex: 'keyword',
            key: 'keyword',
            render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
        },
        {
            title: '站点',
            dataIndex: 'site',
            key: 'site',
            width: 80,
            render: (text: string) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: '搜索日期',
            dataIndex: 'search_date',
            key: 'search_date',
            width: 120,
        },
        {
            title: '结果数',
            dataIndex: 'result_count',
            key: 'result_count',
            width: 100,
            align: 'right' as const,
        },
        {
            title: '导入时间',
            dataIndex: 'imported_at',
            key: 'imported_at',
            width: 180,
            render: (text: string) => new Date(text).toLocaleString('zh-CN'),
        },
        {
            title: '操作',
            key: 'action',
            width: 180,
            render: (_: any, record: KeywordSearchImportType) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<SearchOutlined />}
                        onClick={() => navigate(`/keyword-search/${record.id}`)}
                    >
                        查看详情
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        onClick={() => handleDelete(record.id)}
                    >
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    const handleDelete = async (id: number) => {
        if (!window.confirm('确定要删除这条记录吗？')) return;
        try {
            await keywordSearchAPI.deleteImport(id);
            message.success('删除成功');
            loadImports();
        } catch (error) {
            message.error('删除失败');
        }
    };

    return (
        <Card
            title="关键词搜索结果导入"
            extra={
                <Upload
                    accept=".xlsx,.xls"
                    showUploadList={false}
                    customRequest={handleUpload}
                >
                    <Button type="primary" icon={<UploadOutlined />}>
                        上传搜索结果
                    </Button>
                </Upload>
            }
            style={{ marginTop: 24 }}
        >
            <div style={{ marginBottom: 16, color: '#666', fontSize: '13px' }}>
                <FileExcelOutlined style={{ marginRight: 8 }} />
                支持的文件名格式: <code>Search(关键词)-数量-站点-日期.xlsx</code> (例如: Search(dog-clippers)-98-US-20251203.xlsx)
            </div>

            <Table
                columns={columns}
                dataSource={imports}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 5 }}
                size="small"
            />
        </Card>
    );
};
